import React, { useState, useRef, useEffect, useCallback, createContext, useContext } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, X } from "lucide-react";

/**
 * Building blocks for long-form project write-ups that live on the site itself
 * (rather than linking out). Used by the "story" pages under /projects/*.
 *
 * Wrap a page in <Story> to get the shared image lightbox, then compose with
 * <StoryHero>, <StoryNav>, <Section> and the small primitives below.
 */

const fade = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const LightboxContext = createContext(() => {});

export function Story({ children }) {
  const [lightbox, setLightbox] = useState(null);
  const open = useCallback((v) => setLightbox(v), []);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => e.key === "Escape" && setLightbox(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  return (
    <LightboxContext.Provider value={open}>
      <div className="pb-16">
        {children}

        {lightbox && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
            onClick={() => setLightbox(null)}
          >
            <button
              type="button"
              aria-label="Close"
              className="absolute right-4 top-4 rounded-full border border-border bg-surface/80 p-2 text-muted hover:text-foreground"
              onClick={() => setLightbox(null)}
            >
              <X className="w-5 h-5" />
            </button>
            <img
              src={lightbox.src}
              alt={lightbox.alt}
              className="max-w-full max-h-[92vh] rounded-lg border border-border shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
    </LightboxContext.Provider>
  );
}

/** Full-bleed page header. `image` is an optional faded background photo. */
export function StoryHero({ kicker, title, lead, image, breadcrumb, stats, chips }) {
  return (
    <header className="relative overflow-hidden border-b border-border">
      {image && (
        <div className="absolute inset-0 -z-10">
          <img
            src={image}
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/80 to-background" />
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 md:px-8 pt-10 pb-14 md:pt-14 md:pb-20">
        {breadcrumb && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-8">
            <Link to="/projects" className="hover:text-foreground transition-colors">
              Projects
            </Link>
            <ChevronLeft className="w-3.5 h-3.5 rotate-180" />
            <Link to={breadcrumb.to} className="hover:text-foreground transition-colors">
              {breadcrumb.label}
            </Link>
          </div>
        )}

        {kicker && (
          <p className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-accent/80 mb-4">
            {kicker}
          </p>
        )}
        <h1 className="font-display text-3xl md:text-5xl font-semibold text-foreground leading-tight">
          {title}
        </h1>
        {lead && (
          <p className="mt-6 text-lg text-muted leading-relaxed max-w-2xl">{lead}</p>
        )}

        {stats?.length > 0 && (
          <div
            className={`mt-8 grid gap-3 ${
              stats.length >= 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"
            }`}
          >
            {stats.map((s) => (
              <Stat key={s.label} {...s} />
            ))}
          </div>
        )}

        {chips?.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2">
            {chips.map((c) => (
              <span
                key={c}
                className="rounded-full border border-border bg-surface/50 px-3 py-1 text-xs text-muted-foreground"
              >
                {c}
              </span>
            ))}
          </div>
        )}
      </div>
    </header>
  );
}

/** Sticky in-page section jump bar. `items` is [[id, label], …]. */
export function StoryNav({ items }) {
  return (
    <nav className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <ul className="flex gap-1 overflow-x-auto py-2.5 text-xs [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {items.map(([id, label]) => (
            <li key={id}>
              <a
                href={`#${id}`}
                className="whitespace-nowrap rounded-full px-3 py-1.5 text-muted-foreground hover:bg-surface hover:text-accent transition-colors"
              >
                {label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}

export function Section({ id, kicker, title, lead, children }) {
  return (
    <motion.section
      id={id}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-80px" }}
      variants={{ visible: { transition: { staggerChildren: 0.06 } } }}
      className="scroll-mt-24 border-t border-border/60 py-12 md:py-16"
    >
      {kicker && (
        <motion.p
          variants={fade}
          className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-accent/80 mb-3"
        >
          {kicker}
        </motion.p>
      )}
      <motion.h2
        variants={fade}
        className="font-display text-2xl md:text-3xl font-semibold text-foreground leading-tight"
      >
        {title}
      </motion.h2>
      {lead && (
        <motion.p
          variants={fade}
          className="mt-4 text-muted text-base md:text-lg leading-relaxed max-w-3xl"
        >
          {lead}
        </motion.p>
      )}
      <div className="mt-8 space-y-8">{children}</div>
    </motion.section>
  );
}

/** A zoomable figure with a caption. */
export function Figure({ src, alt, caption, className = "" }) {
  const open = useContext(LightboxContext);
  return (
    <motion.figure variants={fade} className={className}>
      <button
        type="button"
        onClick={() => open({ src, alt })}
        className="block w-full overflow-hidden rounded-lg border border-border bg-surface cursor-zoom-in group"
      >
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className="w-full h-auto transition-transform duration-300 group-hover:scale-[1.01]"
        />
      </button>
      {caption && (
        <figcaption className="mt-2.5 text-sm text-muted-foreground leading-relaxed">
          {caption}
        </figcaption>
      )}
    </motion.figure>
  );
}

/** A video that plays once it scrolls into view and pauses when it leaves. */
export function VideoFigure({ src, caption, badge, controls = true, className = "" }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) el.play().catch(() => {});
        else el.pause();
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <motion.figure variants={fade} className={className}>
      <div className="relative overflow-hidden rounded-lg border border-border bg-[#04141f]">
        <video
          ref={ref}
          src={src}
          muted
          loop
          playsInline
          controls={controls}
          preload="metadata"
          className="w-full h-auto block"
        />
        {badge && (
          <span className="pointer-events-none absolute left-3 top-3 rounded-full border border-accent/30 bg-background/80 px-2.5 py-0.5 font-mono text-[0.65rem] uppercase tracking-wide text-accent">
            {badge}
          </span>
        )}
      </div>
      {caption && (
        <figcaption className="mt-2.5 text-sm text-muted-foreground leading-relaxed">
          {caption}
        </figcaption>
      )}
    </motion.figure>
  );
}

export function Stat({ value, unit, label, amber = false }) {
  return (
    <div className="rounded-lg border border-border bg-surface/60 px-4 py-3.5">
      <div
        className={`font-display text-2xl md:text-3xl font-semibold leading-none ${
          amber ? "text-amber-300" : "text-accent"
        }`}
      >
        {value}
        {unit && <span className="text-base font-normal text-muted ml-1">{unit}</span>}
      </div>
      <div className="mt-1.5 text-xs text-muted-foreground leading-snug">{label}</div>
    </div>
  );
}

export function StatRow({ children, cols = 2 }) {
  return (
    <motion.div
      variants={fade}
      className={`grid gap-3 ${cols === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}
    >
      {children}
    </motion.div>
  );
}

export function Card({ title, children, accent = false }) {
  return (
    <motion.div
      variants={fade}
      className={`rounded-lg border bg-surface/50 p-5 ${
        accent ? "border-accent/30" : "border-border"
      }`}
    >
      {title && (
        <h3 className="font-display text-base font-semibold text-foreground mb-2">{title}</h3>
      )}
      <div className="text-sm text-muted leading-relaxed space-y-2">{children}</div>
    </motion.div>
  );
}

export function Eq({ children }) {
  return (
    <motion.div
      variants={fade}
      className="rounded-lg border border-border bg-surface-elevated/60 px-4 py-3 font-mono text-sm text-foreground overflow-x-auto"
    >
      {children}
    </motion.div>
  );
}

export function Prose({ children }) {
  return (
    <motion.div variants={fade} className="space-y-4 text-muted leading-relaxed">
      {children}
    </motion.div>
  );
}

export function Split({ children, reverse = false }) {
  return (
    <div
      className={`grid gap-6 md:gap-8 md:grid-cols-2 items-start ${
        reverse ? "md:[&>*:first-child]:order-2" : ""
      }`}
    >
      {children}
    </div>
  );
}

export function Bullets({ items }) {
  return (
    <motion.ul variants={fade} className="space-y-2.5">
      {items.map((item, i) => (
        <li key={i} className="flex gap-3 text-muted leading-relaxed">
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent/70" />
          <span>{item}</span>
        </li>
      ))}
    </motion.ul>
  );
}

/** Numbered / lettered step cards, e.g. the stages of an approach. */
export function Steps({ items }) {
  return (
    <motion.div variants={fade} className="grid gap-3 sm:grid-cols-2">
      {items.map(([marker, title, body]) => (
        <div key={marker} className="rounded-lg border border-border bg-surface/50 p-5 flex gap-4">
          <span className="shrink-0 flex h-8 w-8 items-center justify-center rounded-full border border-accent/40 bg-accent/10 font-mono text-sm text-accent">
            {marker}
          </span>
          <div>
            <h3 className="font-display text-base font-semibold text-foreground">{title}</h3>
            <p className="mt-1 text-sm text-muted leading-relaxed">{body}</p>
          </div>
        </div>
      ))}
    </motion.div>
  );
}

/** Closing block: summary paragraphs, tag chips and a link back to the block. */
export function StoryOutro({ title, children, tags, backTo, backLabel, TagComponent }) {
  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={{ visible: { transition: { staggerChildren: 0.06 } } }}
      className="border-t border-border/60 py-12 md:py-16"
    >
      <motion.h2
        variants={fade}
        className="font-display text-2xl md:text-3xl font-semibold text-foreground leading-tight"
      >
        {title}
      </motion.h2>
      <Prose>{children}</Prose>

      {tags?.length > 0 && TagComponent && (
        <motion.div variants={fade} className="mt-10 pt-6 border-t border-border">
          <p className="text-muted-foreground text-xs uppercase tracking-wide mb-3">Tags</p>
          <div className="flex flex-wrap gap-2">
            {tags.map((t) => (
              <TagComponent key={t} tag={t} variant="chip" />
            ))}
          </div>
        </motion.div>
      )}

      {backTo && (
        <motion.div variants={fade} className="mt-8">
          <Link
            to={backTo}
            className="inline-flex items-center gap-2 text-accent hover:text-accent-hover text-sm transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            {backLabel}
          </Link>
        </motion.div>
      )}
    </motion.section>
  );
}

export { fade };
